/**
 * Skeleton Component
 * Reusable skeleton loader with smooth shimmer animation
 * Uses React Native Animated API with useNativeDriver for high performance
 */

import React, { useEffect, useRef, useState } from 'react';
import { View, Animated, StyleSheet, Platform, AccessibilityInfo, type DimensionValue } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../../../src/theme/useTheme';
import { skeletonStyles, getSkeletonContainerStyle } from './Skeleton.styles';

interface SkeletonProps {
  width?: DimensionValue;
  height?: number;
  borderRadius?: number;
  /** Test ID for testing */
  testID?: string;
}

/** Synchronous first-paint read of the OS/browser reduced-motion preference (web only; false elsewhere). */
function prefersReducedMotionSync(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = 20,
  borderRadius = 8,
  testID,
}) => {
  const theme = useTheme();
  const shimmerAnim = useRef(new Animated.Value(0)).current;
  // Respect reduced motion. Seeded synchronously on web so the loop never even starts when a headless
  // browser emulates `prefers-reduced-motion: reduce` (Storybook test-runner) — a perpetual non-native
  // Animated.loop would otherwise keep the story from ever settling and time the test out. a11y win too.
  const [reduceMotion, setReduceMotion] = useState(prefersReducedMotionSync);

  useEffect(() => {
    let mounted = true;
    AccessibilityInfo.isReduceMotionEnabled?.()
      .then((enabled) => { if (mounted) setReduceMotion(Boolean(enabled)); })
      .catch(() => { /* not supported — keep the synchronous seed */ });
    const sub = AccessibilityInfo.addEventListener?.('reduceMotionChanged', (enabled) =>
      setReduceMotion(Boolean(enabled)),
    );
    return () => {
      mounted = false;
      sub?.remove?.();
    };
  }, []);

  useEffect(() => {
    if (reduceMotion) {
      shimmerAnim.setValue(0.5); // static mid-shimmer; no perpetual loop
      return;
    }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmerAnim, {
          toValue: 1,
          duration: 1500,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(shimmerAnim, {
          toValue: 0,
          duration: 0,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ]),
    );
    loop.start();
    return () => loop.stop(); // stop on unmount / when reduced motion turns on
  }, [shimmerAnim, reduceMotion]);

  // Interpolate shimmer position (left to right)
  // Use larger range (-400 to 400) to ensure shimmer covers wide skeletons
  const translateX = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-400, 400],
  });

  // Interpolate shimmer opacity
  const shimmerOpacity = shimmerAnim.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0.3, 0.8, 0.3],
  });

  const containerStyle = getSkeletonContainerStyle(theme, width, height, borderRadius);

  return (
    <View
      style={[skeletonStyles.container, containerStyle]}
      testID={testID || 'skeleton'}
    >
      <Animated.View
        style={[
          StyleSheet.absoluteFill,
          {
            transform: [{ translateX }],
            opacity: shimmerOpacity,
          },
        ]}
      >
        <LinearGradient
          colors={[
            'transparent',
            theme.gray[100] || 'rgba(255, 255, 255, 0.6)',
            'transparent',
          ]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={StyleSheet.absoluteFill}
        />
      </Animated.View>
    </View>
  );
};

